import React from 'react';

import { IPropertyCompany } from 'interfaces/IPropertyCompany';
import { ISearchInputCustomOption } from 'interfaces/ISearchInputCustomOption';
import {
  CombinedSuggestion,
  IBoundarySuggestionInputs,
} from 'interfaces/IBoundarySuggestionInputs';
import { SEARCH_COMPANIES_PROPERTIES_QUERY } from 'graphql/common';
import SearchInput from 'components/SearchInput';
import { SearchResultDataType } from 'components/SearchInput/interfaces';
import { PropertyCompanyType } from 'constants/propertyCompanyType';
import { I18N_AVANT_PROPERTY_LABEL_PATH } from 'constants/i18n';
import locations from 'routes';
import { translateText } from 'utils/i18n';
import { SearchParamsService } from 'services/SearchParamsService';
import AuthService from 'services/Auth';

import { SearchInputSize, SuggestionIcon } from '../SearchInput/enums';

const I18N_COMPANY_PROPERTY_SEARCH_INPUT_LABEL_PATH = `${I18N_AVANT_PROPERTY_LABEL_PATH}.companyPropertySearchInput`;

export type Props = {
  aliasPlaceholder?: (suggestionName: string) => string;
  autofocus?: boolean;
  clearAfterSuggestionClick?: boolean;
  customOption?: ISearchInputCustomOption;
  editValue?: string;
  filterOnlyCompanies?: boolean;
  filterOnlyCities?: boolean;
  filterOnlyProperties?: boolean;
  hasBottomMargin?: boolean;
  hideEmptySuggestionsPressEnter?: boolean;
  ignoreCompanyId?: number;
  includeSubmarkets?: boolean;
  includeMapBoxResults?: boolean;
  includeMarkets?: boolean;
  initialValue?: string;
  inputId?: string;
  isDisabled?: boolean;
  isEditable?: boolean;
  isSearchIconLeftVisible?: boolean;
  navbar?: boolean;
  onChangeText?: (value?: string) => void;
  onClearSelectedSuggestion?: () => void;
  onClickSuggestionCustom?: (suggestion: CombinedSuggestion) => void;
  placeholder?: string;
  removeMergedCompanies?: boolean;
  selectable?: boolean;
  size?: SearchInputSize;
  withMarketSwitch?: boolean;
  wrapperClassName?: string;
  inputContainerClassName?: string;
  suggestionClassName?: string;
  inputBottomChildren?: React.ReactNode;
  fetchGlobalBoundaries?: boolean;
  selectedCountry?: string;
  globalBoundaries?: boolean;
  parseMapboxResult?: (v: SearchResultDataType[]) => IPropertyCompany[];
};

const CompaniesPropertiesSearchInput: React.FC<Props> = props => {
  const {
    aliasPlaceholder,
    autofocus,
    clearAfterSuggestionClick,
    customOption,
    editValue,
    filterOnlyCompanies,
    filterOnlyCities,
    filterOnlyProperties,
    hasBottomMargin,
    hideEmptySuggestionsPressEnter,
    ignoreCompanyId,
    includeSubmarkets = false,
    includeMapBoxResults = false,
    includeMarkets = false,
    initialValue,
    inputId,
    isDisabled,
    isEditable,
    isSearchIconLeftVisible,
    navbar,
    onChangeText,
    onClearSelectedSuggestion,
    onClickSuggestionCustom,
    placeholder = translateText(
      `${I18N_COMPANY_PROPERTY_SEARCH_INPUT_LABEL_PATH}.inputPlaceholder`,
    ),
    removeMergedCompanies,
    selectable,
    size,
    withMarketSwitch,
    wrapperClassName,
    inputContainerClassName,
    suggestionClassName,
    inputBottomChildren,
    fetchGlobalBoundaries,
    selectedCountry,
    globalBoundaries,
    parseMapboxResult,
  } = props;

  const parseMergedCompanyToPropertyCompany = (
    mergeCompanyId: number,
    mergeCompanyName: string,
  ): IPropertyCompany => {
    return {
      id: mergeCompanyId,
      name: mergeCompanyName,
      type: 'company',
      industryName: '',
      headquarters: '',
      companyId: mergeCompanyId,
    };
  };

  const filterSuggestions = (suggestions: SearchResultDataType[]) => {
    if (!ignoreCompanyId && !removeMergedCompanies) return suggestions;

    return (suggestions as IPropertyCompany[]).filter(
      (item: IPropertyCompany) => {
        const validId = ignoreCompanyId
          ? item.companyId !== ignoreCompanyId
          : true;
        const validCompany = removeMergedCompanies
          ? !item.mergedCompanyId
          : true;
        return validId && validCompany;
      },
    );
  };

  const onClickSuggestion = (propertyCompany: IPropertyCompany) => {
    const locationOptions = {
      [PropertyCompanyType.COMPANY]: locations.showCompany(
        propertyCompany?.companyId!,
      ),
      [PropertyCompanyType.PROPERTY]: locations.showProperty(
        propertyCompany?.propertyId!,
      ),
      [PropertyCompanyType.MARKET]: locations.showMarket(
        propertyCompany.marketId!,
      ),
      [PropertyCompanyType.SUBMARKET]: locations.showSubmarket(
        propertyCompany?.submarketId!,
        propertyCompany?.propertyTypeId!,
      ),
      [PropertyCompanyType.MAPBOX_ADDRESS]: locations.showAddressProfileAmenities(
        SearchParamsService.parseMapBoxDetailsToUrlParams(
          propertyCompany?.mapBoxDetail,
          propertyCompany?.displayAddress,
        ),
      ),
    };

    window.location.href =
      locationOptions[propertyCompany.type! as PropertyCompanyType];
  };

  const onClickSearchSuggestion = (value: IPropertyCompany) => {
    const { mergedCompanyId, mergedCompanyName } = value;

    // Check if the value is a merged company, and use it for the click action
    const selectedValue =
      mergedCompanyId && mergedCompanyName
        ? parseMergedCompanyToPropertyCompany(
            mergedCompanyId,
            mergedCompanyName,
          )
        : value;

    !!onClickSuggestionCustom
      ? onClickSuggestionCustom?.(selectedValue)
      : onClickSuggestion(selectedValue);
  };

  async function suggestGlobalBoundaries(
    apiUrl: string,
    requestData: IBoundarySuggestionInputs,
  ): Promise<any[]> {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': AuthService.getOktaToken('avant-functions'),
        'ay-api-auth': 'OKTA',
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();

    return Array.isArray(data)
      ? data.map(suggestion => ({
          name: suggestion.text,
          geoIds: [suggestion.document['GEOID']],
          countryCode: suggestion?.document['COUNTRY_CODE'],
          type: 'globalBoundary',
          geoHierarchyLevel: suggestion?.document['GEOHIERARCHY_LEVEL'],
        }))
      : [];
  }

  return (
    <SearchInput
      aliasPlaceholder={aliasPlaceholder}
      clearAfterSuggestionClick={clearAfterSuggestionClick}
      hasBottomMargin={hasBottomMargin}
      inputId={inputId || 'companies-properties-search-input'}
      isEditable={isEditable}
      navbar={navbar}
      autofocus={autofocus}
      suggestionIconType={SuggestionIcon.dynamicIcon}
      onClickSuggestion={onClickSearchSuggestion}
      onChangeText={onChangeText}
      onClearSelectedSuggestion={onClearSelectedSuggestion}
      resultSelectedComponent={editValue}
      filterSuggestions={filterSuggestions}
      parseMapboxResult={parseMapboxResult}
      inputBottomChildren={inputBottomChildren}
      placeholder={placeholder}
      selectable={selectable}
      initialValue={initialValue}
      hideEmptySuggestionsPressEnter={hideEmptySuggestionsPressEnter}
      size={size}
      isSearchIconLeftVisible={isSearchIconLeftVisible}
      graphqlParams={{
        query: SEARCH_COMPANIES_PROPERTIES_QUERY,
        field: 'name',
        resultDataName: 'propertiesCompanies',
        onlyCompanies: filterOnlyCompanies,
        onlyCities: filterOnlyCities,
        withSubmarkets: includeSubmarkets,
        withMarkets: includeMarkets,
        onlyProperties: filterOnlyProperties,
      }}
      withMarketSwitch={withMarketSwitch}
      parseResults={(data: IPropertyCompany[]): IPropertyCompany[] => {
        return data.map(propertyCompany => ({
          ...propertyCompany,
          ...(filterOnlyCities
            ? { name: `${propertyCompany.name}, ${propertyCompany.state}` }
            : { name: propertyCompany.displayAddress || propertyCompany.name }),
        }));
      }}
      customOption={
        customOption && {
          btnLabel: customOption.btnLabel,
          onSelection: customOption.onSelection,
        }
      }
      wrapperClassName={wrapperClassName}
      suggestionClassName={suggestionClassName}
      inputContainerClassName={inputContainerClassName}
      isDisabled={isDisabled}
      includeMapBoxResults={includeMapBoxResults}
      suggestGlobalBoundaries={suggestGlobalBoundaries}
      fetchGlobalBoundaries={fetchGlobalBoundaries}
      globalBoundaries={globalBoundaries}
      selectedCountry={selectedCountry}
    />
  );
};

export default CompaniesPropertiesSearchInput;
